/**
 * Shared bits for manager discount requests on opportunity products (owner spec 2026-10-25):
 * the opportunity summary (banner + line chips), the linked price ↔ discount fields, and the
 * rule that lets a line keep the price a manager approved.
 *
 * Design: docs/design/DESIGN-2026-10-25-manager-discount-requests.md
 * Backend: backend/routers/discount_requests.py
 */
import React from 'react';
import config from '../../config/config';
import { getSessionToken } from '../../utils/session';
import { ILS } from '../creditApprovals/creditApprovalShared';

export { api, ILS, inputStyle, readOnlyStyle, labelStyle, overlayStyle, modalStyle, btn } from '../creditApprovals/creditApprovalShared';

const API_BASE = config.api.baseUrl;

export const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

/**
 * Pending + approved discounts of one opportunity, or null when the feature is off
 * (flag discount_requests.enabled → 404) or the call failed — then no button, no banner.
 */
export async function fetchOpportunityDiscounts(opportunityId) {
  if (!opportunityId) return null;
  try {
    const token = getSessionToken();
    const res = await fetch(`${API_BASE}/api/discount-requests/opportunity/${encodeURIComponent(opportunityId)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
    return res.ok ? await res.json() : null;
  } catch (e) {
    return null;
  }
}

const parse = (text) => {
  const s = String(text ?? '').replace(/[,₪\s]/g, '');
  return s === '' || !/^-?\d+(\.\d+)?$/.test(s) ? null : Number(s);
};

/**
 * The two linked fields of the request: the requested price per unit and the discount in ₪
 * on the whole line. Typing in one recalculates the other.
 * line = { quantity, total }; field = 'price' | 'discount'; text = what was typed.
 */
export function linkedAmounts(line, field, text) {
  const qty = Number(line && line.quantity) || 1;
  const total = Number(line && line.total) || 0;
  const v = parse(text);
  if (field === 'price') {
    return { price: text, discount: v === null ? '' : String(round2(total - v * qty)) };
  }
  return { discount: text, price: v === null ? '' : String(round2((total - v) / qty)) };
}

/** The ₪ discount a request form would send, or a Hebrew reason it cannot be sent. */
export function requestedDiscount(line, discountText) {
  const d = parse(discountText);
  const total = Number(line && line.total) || 0;
  if (d === null) return { error: 'יש למלא מחיר מבוקש או סכום הנחה' };
  if (d <= 0) return { error: 'ההנחה חייבת להיות גדולה מאפס — המחיר המבוקש נמוך מהמחיר הנוכחי' };
  if (d >= total) return { error: 'ההנחה חייבת להיות קטנה מסך השורה' };
  return { discount: round2(d) };
}

const pendingOf = (summary, lineId) => ((summary && summary.pending) || []).find((p) => p.line_item_id === lineId);
const approvedOf = (summary, lineId) => ((summary && summary.approved) || []).find((a) => a.line_item_id === lineId);

/**
 * A line a manager discounted may keep the approved unit price even below the user's price
 * range — otherwise a later quantity edit would be refused. Only the same product, only that price.
 */
export function mayKeepApprovedPrice(summary, lineId, unitPrice, productChanged) {
  const a = approvedOf(summary, lineId);
  return !!a && !productChanged && Math.abs(Number(unitPrice) - Number(a.approved_unit_price)) < 0.005;
}

export const approvedUnitPrice = (summary, lineId) => {
  const a = approvedOf(summary, lineId);
  return a ? a.approved_unit_price : null;
};

/** Top of the Opportunity page: "⏳ ממתין להנחת מנהל על סך: ₪X". */
export const DiscountPendingBanner = ({ summary }) => {
  if (!summary || !summary.pending_count) return null;
  return (
    <div role="status" data-testid="discount-pending-banner" style={{
      background: '#fff4e5', border: '2px solid #dd7a01', color: '#8c4b02', borderRadius: 8,
      padding: '10px 20px', marginBottom: 10, textAlign: 'center', fontSize: 15, fontWeight: 700,
    }}>
      ⏳ ממתין להנחת מנהל על סך: {ILS(summary.pending_total)}
      {summary.pending_count > 1 && <span style={{ fontWeight: 500 }}> ({summary.pending_count} מוצרים)</span>}
    </div>
  );
};

/** On an OpportunityLineItem row: the pending request, else the latest approved discount. */
export const DiscountLineChip = ({ summary, lineId }) => {
  const p = pendingOf(summary, lineId);
  const a = approvedOf(summary, lineId);
  if (!p && !a) return null;
  const style = {
    padding: '1px 8px', borderRadius: 8, fontSize: 12, fontWeight: 600,
    background: p ? '#fff4e5' : '#e6f4ea', color: p ? '#8c4b02' : '#1d6b3f',
  };
  return (
    <span className="slds-related-record__field" data-testid="discount-line-chip" style={style}>
      {p ? `⏳ הנחה ממתינה ${ILS(p.requested_discount)}` : `💲 הנחת מנהל ${ILS(a.approved_discount)}`}
    </span>
  );
};
